import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus, X, ChevronLeft, ChevronRight, Clock, AlertCircle, CheckCircle2, Circle, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const priorityConfig = {
    high:   { label: 'High',   dot: 'bg-red-500',   badge: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/30', icon: AlertCircle },
    medium: { label: 'Medium', dot: 'bg-amber-400',  badge: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/30', icon: Clock },
    low:    { label: 'Low',    dot: 'bg-emerald-500',badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30', icon: CheckCircle2 },
};

const TaskCalendar = ({ isSidebar = false }) => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newTask, setNewTask] = useState({
        title: '',
        date: new Date().toISOString().split('T')[0],
        priority: 'medium',
        description: '',
        department: ''
    });
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [colleges, setColleges] = useState([]);
    const [degrees, setDegrees] = useState([]);

    useEffect(() => { 
        fetchTasks(); 
        fetchColleges();
        if (user?.role === 'college_admin') {
            const cid = user.college || user.collegeId;
            if (cid) {
                api.get(`/college-degrees?college=${cid}`)
                    .then(res => {
                        if (res.data.success) setDegrees(res.data.data);
                    })
                    .catch(e => console.error('Failed to fetch college degrees', e));
            }
        }
    }, [user]);

    const fetchColleges = async () => {
        try {
            const { data } = await api.get('/colleges?limit=1000');
            if (data.success) setColleges(data.data);
        } catch (e) {
            console.error('Failed to fetch colleges', e);
        }
    };

    const fetchTasks = async () => {
        try {
            const { data } = await api.get('/analytics/calendar-events');
            if (data.success) setTasks(data.data);
        } catch (e) {
            console.error('Failed to fetch calendar events', e);
        }
    };

    const getDaysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay();

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        setSelectedDate(null);
    };
    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        setSelectedDate(null);
    };

    const dateStr = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    const getTasksForDate = (y, m, d) => tasks.filter(t => t.date === dateStr(y, m, d));

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const getTodayTasks = () => tasks.filter(t => t.date === todayStr);
    const getUpcomingTasks = () => tasks.filter(t => t.date > todayStr).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6);

    const selectedDateTasks = selectedDate
        ? getTasksForDate(currentDate.getFullYear(), currentDate.getMonth(), selectedDate)
        : [];

    const handleAddOrEditTask = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...newTask };

            if (editingTaskId) {
                const { data } = await api.put(`/tasks/${editingTaskId}`, payload);
                if (data.success) {
                    toast.success('Event updated successfully');
                }
            } else {
                const { data } = await api.post('/tasks', payload);
                if (data.success) {
                    toast.success('Event added successfully');
                }
            }
            setIsModalOpen(false);
            setNewTask({ title: '', date: new Date().toISOString().split('T')[0], priority: 'medium', description: '', department: '' });
            setEditingTaskId(null);
            fetchTasks();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save event');
        }
    };

    const handleDeleteTask = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        try {
            const taskId = id.replace('task-', '');
            const { data } = await api.delete(`/tasks/${taskId}`);
            if (data.success) {
                toast.success('Event deleted successfully');
                fetchTasks();
            }
        } catch (err) {
            toast.error('Failed to delete event');
        }
    };

    const openModalForDate = (day) => {
        setEditingTaskId(null);
        setNewTask({ title: '', date: dateStr(currentDate.getFullYear(), currentDate.getMonth(), day), priority: 'medium', description: '', department: '' });
        setIsModalOpen(true);
    };

    const handleEditClick = (e, task) => {
        e.stopPropagation();
        const taskId = task.id.replace('task-', '');
        setEditingTaskId(taskId);
        setNewTask({
            title: task.title,
            date: task.date,
            priority: task.priority || 'medium',
            description: task.description || '',
            department: task.department || ''
        });
        setIsModalOpen(true);
    };

    const handleDayClick = (cell) => {
        if (cell.isCurrentMonth) {
            setSelectedDate(selectedDate === cell.day ? null : cell.day);
        } else if (cell.isPrevMonth) {
            const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            setCurrentDate(prevDate);
            setSelectedDate(cell.day);
        } else if (cell.isNextMonth) {
            const nextDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
            setCurrentDate(nextDate);
            setSelectedDate(cell.day);
        }
    };

    const totalDays = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    // Calculate Previous Month Days (Faded)
    const prevMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const prevMonthTotalDays = getDaysInMonth(prevMonthDate);
    const prevMonthDays = [];
    for (let i = firstDay - 1; i >= 0; i--) {
        prevMonthDays.push({
            day: prevMonthTotalDays - i,
            isCurrentMonth: false,
            isPrevMonth: true,
            isNextMonth: false,
            date: new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, prevMonthTotalDays - i)
        });
    }

    // Calculate Current Month Days
    const currentMonthDays = [];
    for (let i = 1; i <= totalDays; i++) {
        currentMonthDays.push({
            day: i,
            isCurrentMonth: true,
            isPrevMonth: false,
            isNextMonth: false,
            date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i)
        });
    }

    // Calculate Next Month Days (Faded)
    const totalCells = firstDay + totalDays;
    const totalCellsNeeded = totalCells > 35 ? 42 : 35;
    const nextMonthDaysToShow = totalCellsNeeded - totalCells;
    const nextMonthDays = [];
    for (let i = 1; i <= nextMonthDaysToShow; i++) {
        nextMonthDays.push({
            day: i,
            isCurrentMonth: false,
            isPrevMonth: false,
            isNextMonth: true,
            date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i)
        });
    }

    const allGridDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

    return (
        <div className={isSidebar ? "flex flex-col gap-4 w-full" : "grid grid-cols-1 xl:grid-cols-3 gap-6"}>

            {/* ── LEFT: Calendar ── */}
            <div className={`${isSidebar ? "" : "xl:col-span-2"} bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/25 shadow-sm overflow-hidden`}>

                {/* Month nav */}
                <div className={`flex items-center justify-between border-b border-gray-100 dark:border-[#1a3884]/20 ${isSidebar ? "px-4 py-3" : "px-6 py-4"}`}>
                    <div>
                        <h2 className={`${isSidebar ? "text-[15px] font-extrabold" : "text-[18px] font-black"} text-[#1a3884] dark:text-[#4f7dff] tracking-tight`}>
                            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </h2>
                        <p className={`${isSidebar ? "text-[10px]" : "text-[12px]"} text-gray-400 dark:text-gray-500 font-medium mt-0.5`}>
                            {tasks.length} event{tasks.length !== 1 ? 's' : ''} this calendar
                        </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {isSidebar ? (
                            <>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    type="button"
                                    className="p-1.5 bg-[#1a3884] hover:bg-[#132A55] active:scale-95 text-white rounded-lg shadow-md transition-all flex items-center justify-center"
                                    title="Add Event"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 dark:text-gray-400 transition-colors">
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 dark:text-gray-400 transition-colors">
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    type="button"
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#1a3884] hover:bg-[#132A55] active:scale-95 text-white rounded-xl shadow-md transition-all"
                                >
                                    <Plus className="w-4 h-4" /> Add Event
                                </button>
                                <button type="button" onClick={prevMonth} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 dark:text-gray-400 transition-colors">
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={nextMonth} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 dark:text-gray-400 transition-colors">
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 border-b border-gray-100 dark:border-[#1a3884]/20">
                    {DAYS.map(d => (
                        <div key={d} className={`text-center font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider ${isSidebar ? "py-2 text-[9px]" : "py-3 text-[11px]"}`}>
                            {isSidebar ? d[0] : d}
                        </div>
                    ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                    {allGridDays.map((cell, index) => {
                        const colIndex = index % 7;
                        const isLastCol = colIndex === 6;
                        const isToday = cell.isCurrentMonth &&
                            cell.day === today.getDate() &&
                            currentDate.getMonth() === today.getMonth() &&
                            currentDate.getFullYear() === today.getFullYear();
                        const isSelected = cell.isCurrentMonth && selectedDate === cell.day;
                        const dayTasks = getTasksForDate(cell.date.getFullYear(), cell.date.getMonth(), cell.day);
                        const isPast = cell.date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

                        return (
                            <div
                                key={`${cell.isCurrentMonth ? 'c' : cell.isPrevMonth ? 'p' : 'n'}-${cell.day}-${index}`}
                                onClick={() => handleDayClick(cell)}
                                className={`${isSidebar ? "h-11 pt-1.5" : "h-16 pt-2"} border-b border-r dark:border-[#1a3884]/10 flex flex-col items-center gap-0.5 cursor-pointer transition-all relative group
                                    ${isLastCol ? 'border-r-0' : ''}
                                    ${isSelected ? 'bg-[#1a3884]/8 dark:bg-[#4f7dff]/8' : 'hover:bg-gray-50 dark:hover:bg-[#1a3884]/10'}
                                    ${!cell.isCurrentMonth ? 'opacity-30' : ''}
                                    ${cell.isCurrentMonth && isPast && !isToday ? 'opacity-65' : ''}
                                    border-gray-50`}
                            >
                                <span className={`flex items-center justify-center font-bold transition-all
                                    ${isSidebar ? "w-6 h-6 rounded-lg text-[11px]" : "w-8 h-8 rounded-xl text-[13px]"}
                                    ${isToday
                                        ? 'bg-[#1a3884] dark:bg-[#4f7dff] text-white shadow-md shadow-[#1a3884]/30'
                                        : isSelected
                                            ? 'bg-[#1a3884]/15 text-[#1a3884] dark:text-[#4f7dff]'
                                            : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    {cell.day}
                                </span>
                                {/* Event dots */}
                                {dayTasks.length > 0 && (
                                    <div className={`flex items-center ${isSidebar ? "gap-0.5 mt-0.5" : "gap-0.5"}`}>
                                        {dayTasks.slice(0, 3).map((t, idx) => (
                                            <span
                                                key={idx}
                                                className={`${isSidebar ? "w-1 h-1" : "w-1.5 h-1.5"} rounded-full ${priorityConfig[t.priority]?.dot || 'bg-[#4f7dff]'}`}
                                            />
                                        ))}
                                        {dayTasks.length > 3 && (
                                            <span className={`${isSidebar ? "text-[7px]" : "text-[9px]"} font-bold text-gray-400`}>+{dayTasks.length - 3}</span>
                                        )}
                                    </div>
                                )}
                                {/* Quick-add on hover (only in full mode) */}
                                {!isSidebar && cell.isCurrentMonth && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openModalForDate(cell.day); }}
                                        type="button"
                                        className="absolute bottom-1 right-1 w-5 h-5 rounded-md bg-[#1a3884] text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity shadow-sm"
                                    >
                                        <Plus className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className={`flex items-center border-t border-gray-100 dark:border-[#1a3884]/20 ${isSidebar ? "gap-3.5 px-4 py-2" : "gap-5 px-6 py-3"}`}>
                    {Object.entries(priorityConfig).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-1.5">
                            <span className={`rounded-full ${isSidebar ? "w-1.5 h-1.5" : "w-2 h-2"} ${val.dot}`} />
                            <span className={`font-medium text-gray-400 capitalize ${isSidebar ? "text-[10px]" : "text-[11px]"}`}>{val.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── RIGHT: Sidebar ── */}
            <div className="flex flex-col gap-4">

                {/* Selected day events */}
                {selectedDate && (
                    <div className="bg-white dark:bg-[#002147] rounded-2xl border border-[#1a3884]/20 dark:border-[#4f7dff]/20 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-[#1a3884]/20">
                            <div>
                                <p className="text-[13px] font-black text-[#1a3884] dark:text-[#4f7dff]">
                                    {MONTHS[currentDate.getMonth()]} {selectedDate}
                                </p>
                                <p className="text-[11px] text-gray-400">{selectedDateTasks.length} event{selectedDateTasks.length !== 1 ? 's' : ''}</p>
                            </div>
                            <button onClick={() => setSelectedDate(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 text-gray-400 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 space-y-2">
                            {selectedDateTasks.length === 0 ? (
                                <div className="text-center py-6">
                                    <CalendarIcon className="w-8 h-8 text-gray-200 dark:text-[#1a3884]/40 mx-auto mb-2" />
                                    <p className="text-[12px] text-gray-400">No events on this day</p>
                                    <button
                                        onClick={() => openModalForDate(selectedDate)}
                                        className="mt-3 text-[12px] font-bold text-[#1a3884] dark:text-[#4f7dff] hover:underline"
                                    >
                                        + Add event
                                    </button>
                                </div>
                            ) : selectedDateTasks.map((task, idx) => {
                                const cfg = priorityConfig[task.priority] || priorityConfig.medium;
                                return (
                                    <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-[#001f40] border border-gray-100 dark:border-[#1a3884]/20">
                                        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13px] font-bold text-gray-900 dark:text-white truncate">{task.title}</p>
                                            {task.description && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{task.description}</p>}
                                            <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                                            {(task.creatorRole === 'superadmin' || task.creatorRole === 'admin') && <span className="inline-block mt-1.5 ml-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Smaart Admin</span>}
                                            {task.creatorRole === 'college_admin' && <span className="inline-block mt-1.5 ml-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">College Admin</span>}
                                        </div>
                                        {task.type === 'task' && (!task.createdBy || task.createdBy === user?._id || user?.role === 'superadmin' || user?.role === 'admin') && (
                                            <div className="flex flex-col gap-1 shrink-0 ml-1">
                                                <button onClick={(e) => handleEditClick(e, task)} className="p-1 text-gray-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors rounded">
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={(e) => handleDeleteTask(e, task.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Merged Upcoming & Today Events */}
                <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/25 shadow-sm overflow-hidden flex flex-col">
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-[#1a3884]/20 shrink-0">
                        <Clock className="w-3.5 h-3.5 text-[#1a3884] dark:text-[#4f7dff]" />
                        <h3 className="text-[13px] font-black text-gray-700 dark:text-gray-200">Upcoming Events</h3>
                        {getTodayTasks().length + getUpcomingTasks().length > 0 && (
                            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-[#1a3884]/10 text-[#1a3884] dark:text-[#4f7dff] dark:bg-[#4f7dff]/10 rounded-full">
                                {getTodayTasks().length + getUpcomingTasks().length}
                            </span>
                        )}
                    </div>
                    <div className="p-4 space-y-2 overflow-y-auto max-h-[380px]">
                        {getTodayTasks().length === 0 && getUpcomingTasks().length === 0 ? (
                            <div className="flex items-center gap-2 py-3">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <p className="text-[12px] text-gray-400">All clear, no upcoming events!</p>
                            </div>
                        ) : [...getTodayTasks(), ...getUpcomingTasks()].map((task, idx) => {
                            const cfg = priorityConfig[task.priority] || priorityConfig.medium;
                            const isTodayEvent = task.date === todayStr;
                            const d = new Date(task.date);

                            return (
                                <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl transition-colors border ${isTodayEvent ? 'bg-gray-50 dark:bg-[#001f40] border-gray-100 dark:border-[#1a3884]/20' : 'hover:bg-gray-50 dark:hover:bg-[#001f40] border-transparent hover:border-gray-100 dark:hover:border-[#1a3884]/20'}`}>
                                    {isTodayEvent ? (
                                        <div className="shrink-0 w-9 h-9 rounded-xl bg-green-500/10 flex flex-col items-center justify-center relative overflow-hidden">
                                            <div className="absolute inset-0 bg-green-500/20 animate-pulse"></div>
                                            <span className="text-[9px] font-bold text-green-600 uppercase leading-none z-10">
                                                Tdy
                                            </span>
                                            <span className="text-[14px] font-black text-green-600 leading-tight z-10">
                                                {d.getDate()}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="shrink-0 w-9 h-9 rounded-xl bg-[#1a3884]/8 dark:bg-[#4f7dff]/10 flex flex-col items-center justify-center">
                                            <span className="text-[9px] font-bold text-[#1a3884] dark:text-[#4f7dff] uppercase leading-none">
                                                {MONTHS[d.getMonth()].slice(0, 3)}
                                            </span>
                                            <span className="text-[14px] font-black text-[#1a3884] dark:text-[#4f7dff] leading-tight">
                                                {d.getDate()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            {isTodayEvent && <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
                                            <p className="text-[13px] font-bold text-gray-900 dark:text-white truncate">{task.title}</p>
                                        </div>
                                        <span className={`inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                                        {(task.creatorRole === 'superadmin' || task.creatorRole === 'admin') && <span className="inline-block mt-0.5 ml-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Smaart Admin</span>}
                                        {task.creatorRole === 'college_admin' && <span className="inline-block mt-0.5 ml-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">College Admin</span>}
                                    </div>
                                    {task.type === 'task' && (!task.createdBy || task.createdBy === user?._id || user?.role === 'superadmin' || user?.role === 'admin') && (
                                        <div className="flex flex-col gap-1 shrink-0 ml-1">
                                            <button onClick={(e) => handleEditClick(e, task)} className="p-1 text-gray-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors rounded">
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={(e) => handleDeleteTask(e, task.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── Modal ── */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-gray-100 dark:border-[#1a3884]/30 overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1a3884]/20">
                            <div>
                                <h3 className="text-[16px] font-black text-[#1a3884] dark:text-[#4f7dff]">
                                    {editingTaskId ? 'Edit Event' : 'Add New Event'}
                                </h3>
                                <p className="text-[12px] text-gray-400 mt-0.5">Schedule an academic event or task</p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 text-gray-400 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAddOrEditTask} className="p-6 space-y-5">
                            <div>
                                <label className="block text-[12px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1.5">
                                    Event Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={newTask.title}
                                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent outline-none transition-all text-sm"
                                    placeholder="e.g. Mid-semester exams begin"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[12px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1.5">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={newTask.date}
                                        onChange={(e) => setNewTask({ ...newTask, date: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent outline-none transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1.5">
                                        Priority
                                    </label>
                                    <select
                                        value={newTask.priority}
                                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent outline-none transition-all text-sm"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[12px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1.5">
                                    Description
                                </label>
                                <textarea
                                    rows="3"
                                    value={newTask.description}
                                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent outline-none transition-all text-sm resize-none"
                                    placeholder="Optional — add notes about this event"
                                />
                            </div>

                            {user?.role === 'college_admin' && (
                                <div>
                                    <label className="block text-[12px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1.5">
                                        Department (Optional)
                                    </label>
                                    <select
                                        value={newTask.department || ''}
                                        onChange={(e) => setNewTask({ ...newTask, department: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent outline-none transition-all text-sm"
                                    >
                                        <option value="">All Departments</option>
                                        {degrees.map(d => (
                                            <option key={d._id} value={d._id}>
                                                {d.abbreviation ? `${d.abbreviation} - ${d.specialization || d.fullName}` : d.fullName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 rounded-xl text-sm font-bold bg-[#1a3884] hover:bg-[#132A55] text-white shadow-md transition-all active:scale-95"
                                >
                                    {editingTaskId ? 'Save Changes' : 'Add Event'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskCalendar;
